/**
 * Categories: what a bank line is *for*.
 *
 * Deliberately separate from the ledger account. People think in categories
 * ("medical consumables"), the ledger thinks in accounts, and the mapping
 * between them is configuration that can change without recategorising
 * history.
 *
 * A category with no account mapped is still useful — it says what a line is —
 * but it posts to the uncategorised head, because the engine has nowhere else
 * to send it. The form says so rather than letting somebody find out at the
 * month end.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Account, type BankCategory } from '../api'
import { Card, ErrorNote, Field, Note } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

const DIRECTIONS = [
  { value: '', label: 'Either — money in or out' },
  { value: 'debit', label: 'Money out' },
  { value: 'credit', label: 'Money in' },
]

/** `Medical consumables` becomes `MEDICAL_CONSUMABLES`, which is what a code is for. */
function codeFrom(name: string): string {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40)
}

export default function AddCategory({
  categories,
  chart,
}: {
  categories: BankCategory[]
  chart: Account[]
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [direction, setDirection] = useState('')
  const [glAccountId, setGlAccountId] = useState('')
  const [done, setDone] = useState<string | null>(null)

  /**
   * Income and expense heads, which is what a bank line is almost always for.
   * Balance-sheet accounts stay reachable at the bottom: a loan repayment is a
   * real bank line and a real liability movement, and a list that hid it would
   * be enforcing a rule the books do not have.
   */
  const postable = useMemo(() => {
    const free = chart.filter((a) => a.is_postable && a.is_active)
    const isPandL = (a: Account) =>
      a.account_type === 'expense' || a.account_type === 'income'
    return [...free.filter(isPandL), ...free.filter((a) => !isPandL(a))]
  }, [chart])

  const code = codeFrom(name)
  const clash = categories.some((c) => c.code === code)

  const create = useMutation({
    mutationFn: () =>
      api.post<BankCategory>('/banking/categories', {
        code,
        name: name.trim(),
        direction: direction || null,
        gl_account_id: glAccountId || null,
      }),
    onSuccess: (row) => {
      setDone(row.name)
      setName('')
      setGlAccountId('')
      void queryClient.invalidateQueries({ queryKey: ['bank-categories'] })
    },
  })

  const ready = name.trim().length > 0 && code.length > 0 && !clash

  return (
    <Card
      title="Categories"
      hint={`${categories.length} so far — what a line is for, not which account it hits`}
    >
      <ErrorNote error={create.error} />
      {done && <Note kind="ok">{done} is ready to use on the lines below.</Note>}

      {categories.length === 0 && (
        <Note kind="info">
          None yet, so every line will post to the account whose name asks the question.
          That is deliberate and not a problem to leave for long: an expense report full of
          “not yet categorised” answers nothing.
        </Note>
      )}

      <p className="muted" style={{ marginTop: 0 }}>
        A line paid out of the bank moves two accounts: the bank itself, which the
        system knows, and whatever the money was <em>for</em>, which it cannot know.
        Pay a medical store ₹1,250 by UPI and the bank falls by ₹1,250; “posts to”
        decides that the other ₹1,250 shows up as consumables rather than as a
        question.
      </p>

      <div className="row">
        <Field label="Name" caption="what somebody would call it out loud">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Medical consumables"
          />
        </Field>
        <Field label="Applies to">
          <select value={direction} onChange={(e) => setDirection(e.target.value)}>
            {DIRECTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Posts to"
          caption="every line has two sides — the bank is one, this is the other"
        >
          <SearchSelect
            aria-label="Ledger account"
            value={glAccountId}
            onChange={setGlAccountId}
            empty="Not decided — lands in “not yet categorised”"
            options={postable.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))}
          />
        </Field>
      </div>

      {clash && (
        <Note kind="warn">
          There is already a category with the code <span className="mono">{code}</span>.
          Give this one a different name.
        </Note>
      )}
      {!clash && name.trim() && !glAccountId && (
        <Note kind="warn">
          With no account chosen, lines in this category still post — money out to{' '}
          <span className="mono">6900 Expenses not yet categorised</span> and money in to{' '}
          <span className="mono">4990 Income not yet categorised</span>. The category will
          say what they were for; the profit and loss will not. You can set the account
          later, though only lines posted after that follow it.
        </Note>
      )}

      <button
        className="primary"
        disabled={!ready || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? 'Adding…' : `Add ${code || 'category'}`}
      </button>

      {categories.length > 0 && (
        <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
          {categories.map((c) => c.name).join(' · ')}
        </p>
      )}
    </Card>
  )
}
